import { useSelector } from 'react-redux';

import getWholesaler from '@/API/Wholesaler/getWholesaler';
import config from '@/config.json';
import { useQuery } from 'hooks/useCustomQuery';

/**
 * The currently selected wholesaler's record, straight from `/wholesaler/{id}`.
 *
 * Replaces reading `navigationLists.currentWholesalerData`. Keyed to match
 * `useGetWholesalerByID` (2807) so both share one cache entry and one request.
 *
 * Only fetched at wholesaler scope or above — sub-wholesaler users have no
 * access to the endpoint, and the pages that need the name are wholesaler-scope
 * pages anyway.
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
const useGetCurrentWholesaler = () => {
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    return useQuery({
        queryKey: ['wholesaler', currentWholesaler],
        queryFn: () => getWholesaler(currentWholesaler),
        enabled:
            Boolean(currentWholesaler) &&
            requiredScope >= config.scope.wholesaler,
        // The id is what callers match against the selected wholesaler, so it
        // must always be present even if the payload omits it.
        select: (data) =>
            data ? { ...data, id: data?.id || currentWholesaler } : data,
        onError: (error) => {
            console.error('Failed to load current wholesaler:', error);
        },
        refetchOnWindowFocus: false,
    });
};

export default useGetCurrentWholesaler;
